import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AlertCircle, TrendingUp, Shield, Clock, CheckCircle, FileText, Zap } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroAccent } from '@/components/shared/PageHero';
import FreeReportModal from '@/components/shared/FreeReportModal';
import { generateFAQPageSchema, generateServiceSchema, generateMultipleSchemas } from '@/lib/schema';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';

const AICareerAnalysis = () => {
  const analytics = useAnalytics();
  const [selectedOccupation, setSelectedOccupation] = useState<{title: string; soc: string} | null>(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedArea] = useState('United States');

  // Track page view
  useEffect(() => {
    analytics.trackAIAnalysisView('ai_career_analysis_landing');
  }, [analytics]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch search results
  const { data: searchResults } = useQuery({
    queryKey: ['ai-occupation-search', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery.trim()) return [];
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body: { search: debouncedQuery, limit: 5 }
      });
      if (error) throw error;
      return data?.occupations || [];
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000 // reuse suggestion results within a session
  });

  // Sample AI risk data for preview (using Software Developer as example)
  const sampleAIRisk = {
    riskScore: 45,
    riskLevel: 'Moderate' as const,
    timeline: {
      nearTerm: 'Low',
      mediumTerm: 'Moderate',
      longTerm: 'Moderate'
    },
    factors: [
      'AI augmentation likely, full replacement less likely',
      'Knowledge-based work - moderate automation risk'
    ]
  };

  const handleGetReport = () => {
    if (selectedOccupation) {
      analytics.trackAIAnalysisCTAClick('hero_cta', selectedOccupation.soc);
      setShowPDFModal(true);
    } else {
      analytics.trackAIAnalysisCTAClick('hero_cta_no_occupation');
      // Scroll to search section
      document.getElementById('occupation-search')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: AlertCircle,
      title: 'AI Risk Scoring',
      description: 'Get a 0-100% risk score based on occupation characteristics, automation trends, and industry analysis'
    },
    {
      icon: Clock,
      title: 'Timeline Estimates',
      description: 'Understand when automation might impact your field: near-term (1-3 years), medium-term (3-7 years), and long-term (7-15 years)'
    },
    {
      icon: Shield,
      title: 'Adaptation Strategies',
      description: 'Receive personalized recommendations for skills development and career transitions based on your risk profile'
    },
    {
      icon: TrendingUp,
      title: 'Key Risk Factors',
      description: 'Learn what specific factors contribute to automation risk in your occupation'
    }
  ];

  const faqItems = [
    {
      question: 'What is AI Replacement Risk Analysis?',
      answer: 'AI Replacement Risk Analysis evaluates how likely your occupation is to be impacted by artificial intelligence and automation. We analyze factors like task routineness, physical requirements, human interaction needs, and industry trends to provide a comprehensive risk assessment with timeline estimates and adaptation strategies.'
    },
    {
      question: 'How is AI risk calculated?',
      answer: 'Our AI risk scoring algorithm considers multiple factors: occupation classification (blue collar vs white collar), specific SOC code characteristics, job title keywords, and industry trends. The score ranges from 0-100%, with lower scores indicating lower automation risk. We also provide timeline estimates for near-term (1-3 years), medium-term (3-7 years), and long-term (7-15 years) impacts.'
    },
    {
      question: 'What do the timeline estimates mean?',
      answer: 'Timeline estimates show the expected level of AI/automation impact over different time horizons. "Minimal" means very low risk, "Low" indicates some potential impact, "Moderate" suggests significant changes are possible, and "High" indicates substantial automation risk. These estimates help you plan career transitions and skill development proactively.'
    },
    {
      question: 'How can I use AI risk analysis for career planning?',
      answer: 'Use AI risk analysis to: (1) Identify when to start developing new skills, (2) Plan career transitions before automation impacts your field, (3) Choose specializations within your field that are less vulnerable to automation, (4) Make informed decisions about education and training investments, and (5) Position yourself for roles that complement AI rather than compete with it.'
    },
    {
      question: 'Is AI risk analysis really free?',
      answer: 'Yes. The on-screen AI risk preview is free, and the comprehensive AI Replacement Risk Analysis PDF - with risk scoring, timeline estimates, key factors, and personalized adaptation strategies - is also completely free. Just enter your email and we\'ll deliver the full report to your inbox.'
    },
    {
      question: 'How accurate is the AI risk analysis?',
      answer: 'Our analysis is based on current research on automation trends, occupation characteristics, and industry patterns. While we can\'t predict the future with certainty, our methodology considers multiple data points to provide a well-informed assessment. We recommend using it as one tool in your career planning toolkit, alongside market research and professional advice.'
    }
  ];

  const testimonials = [
    {
      quote: 'The AI risk timeline helped me plan my career transition 3 years ahead. I was able to upskill before automation hit my field.',
      author: 'Jennifer K., Data Analyst',
      role: 'Career Transitioner'
    },
    {
      quote: 'Understanding my occupation\'s AI risk score gave me confidence to negotiate for roles that complement AI rather than compete with it.',
      author: 'Michael R., Software Engineer',
      role: 'Tech Professional'
    }
  ];

  // Generate schema markup
  const baseUrl = 'https://wageatlas.com';
  const currentUrl = `${baseUrl}/ai-career-analysis`;
  
  const faqSchema = generateFAQPageSchema(faqItems);
  const serviceSchema = generateServiceSchema(
    'AI Replacement Risk Analysis',
    'Comprehensive AI automation risk analysis for occupations with risk scoring, timeline estimates, and adaptation strategies.',
    baseUrl,
    '0.00',
    'USD',
    currentUrl
  );
  
  const schemas = generateMultipleSchemas([faqSchema, serviceSchema]);

  return (
    <>
      <SeoHead
        title="AI Job Risk Analysis by Occupation | Future-Proof Your Career | Wage Atlas"
        description="Get free AI replacement risk analysis for any occupation. Understand automation impact, timeline estimates, and adaptation strategies. Free PDF reports delivered to your inbox."
        canonical="https://wageatlas.com/ai-career-analysis/"
        schemas={schemas}
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <PageHero
          badge={{ icon: Zap, label: 'AI-Powered Career Insights' }}
          title={
            <>
              Understand Your Career's
              <HeroAccent> AI Risk Profile</HeroAccent>
            </>
          }
          subtitle="Get comprehensive AI replacement risk analysis for any occupation. Understand automation impact, timeline estimates, and adaptation strategies."
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-gray-100"
              onClick={handleGetReport}
            >
              <FileText className="w-5 h-5 mr-2" />
              Get Your Free AI Risk Report
            </Button>
          </div>
          <p className="text-sm text-white/70">
            100% free. Delivered to your inbox. All salary data and tools on Wage Atlas are free.
          </p>
        </PageHero>

        {/* Interactive Preview Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">See Your AI Risk Analysis</h2>
              <p className="text-xl text-muted-foreground">
                Preview what you'll get in your comprehensive PDF report
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Risk Meter Visualization */}
              <Card>
                <CardHeader>
                  <CardTitle>AI Risk Score</CardTitle>
                  <CardDescription>Sample analysis for Software Developer</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Risk Meter */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Risk Level</span>
                      <Badge variant={sampleAIRisk.riskLevel === 'Low' ? 'default' : sampleAIRisk.riskLevel === 'Moderate' ? 'secondary' : 'destructive'}>
                        {sampleAIRisk.riskLevel}
                      </Badge>
                    </div>
                    <div className="w-full bg-muted rounded-full h-6 relative overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          sampleAIRisk.riskScore < 30 ? 'bg-green-500' :
                          sampleAIRisk.riskScore < 50 ? 'bg-yellow-500' :
                          sampleAIRisk.riskScore < 75 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${sampleAIRisk.riskScore}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
                        {sampleAIRisk.riskScore}% Risk
                      </div>
                    </div>
                  </div>

                  {/* Timeline Breakdown */}
                  <div className="space-y-3">
                    <h4 className="font-semibold">Timeline Estimates:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Near-term (1-3 years):</span>
                        <Badge variant="outline">{sampleAIRisk.timeline.nearTerm} risk</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Medium-term (3-7 years):</span>
                        <Badge variant="outline">{sampleAIRisk.timeline.mediumTerm} risk</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Long-term (7-15 years):</span>
                        <Badge variant="outline">{sampleAIRisk.timeline.longTerm} risk</Badge>
                      </div>
                    </div>
                  </div>

                  {/* Key Factors */}
                  <div className="space-y-2">
                    <h4 className="font-semibold">Key Risk Factors:</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {sampleAIRisk.factors.map((factor, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{factor}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Feature Highlights */}
              <Card>
                <CardHeader>
                  <CardTitle>What's Included</CardTitle>
                  <CardDescription>Complete AI risk analysis in every PDF report</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {features.map((feature, i) => {
                      const Icon = feature.icon;
                      return (
                        <div key={i} className="flex gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                          </div>
                          <div>
                            <h4 className="font-semibold mb-1">{feature.title}</h4>
                            <p className="text-sm text-muted-foreground">{feature.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Occupation Search */}
            <Card id="occupation-search">
              <CardHeader>
                <CardTitle>Get Your AI Risk Analysis</CardTitle>
                <CardDescription>Search for any occupation to get started</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search for an occupation (e.g., 'software developer', 'nurse', 'data analyst')..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {searchResults && searchResults.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {searchResults.map((occupation: unknown) => (
                      <div
                        key={occupation.occupation_code}
                        className="p-3 hover:bg-muted cursor-pointer"
                        onClick={() => {
                          setSelectedOccupation({ 
                            title: occupation.occ_title, 
                            soc: occupation.occupation_code 
                          });
                          setSearchQuery('');
                          analytics.trackAIAnalysisCTAClick('occupation_search', occupation.occupation_code);
                        }}
                      >
                        <div className="font-medium">{occupation.occ_title}</div>
                        <div className="text-sm text-muted-foreground">SOC: {occupation.occupation_code}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedOccupation && (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm mb-2">
                      <strong>Selected:</strong> {selectedOccupation.title}
                    </p>
                    <Button onClick={() => setShowPDFModal(true)} className="w-full">
                      Get Free AI Risk Report for {selectedOccupation.title}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why AI Risk Analysis Matters</h2>
              <p className="text-xl text-muted-foreground">
                Stay ahead of automation and make informed career decisions
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="text-center">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-primary" />
                  <h3 className="font-semibold mb-2">Plan Ahead</h3>
                  <p className="text-sm text-muted-foreground">
                    Understand when automation might impact your field and plan career transitions proactively
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="text-center">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-primary" />
                  <h3 className="font-semibold mb-2">Future-Proof Your Career</h3>
                  <p className="text-sm text-muted-foreground">
                    Get personalized adaptation strategies to position yourself for roles that complement AI
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="text-center">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-primary" />
                  <h3 className="font-semibold mb-2">Make Informed Decisions</h3>
                  <p className="text-sm text-muted-foreground">
                    Use data-driven insights to guide education, training, and career path choices
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Trusted by Professionals</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, i) => (
                <Card key={i}>
                  <CardContent>
                    <blockquote className="text-lg mb-4">"{testimonial.quote}"</blockquote>
                    <div className="font-semibold">{testimonial.author}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-muted-foreground">
                Everything you need to know about AI Risk Analysis
              </p>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* AI economy cross-link: risk is one side, opportunity is the other */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className="p-4 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2">AI doesn't just take jobs - it creates them</h2>
                  <p className="text-muted-foreground">
                    BLS projects data scientist roles to grow 33.5% by 2034, and the AI infrastructure
                    buildout is hiring electricians and HVAC techs at $60k-$92k - no degree required.
                    See every AI-economy career with real government salary data.
                  </p>
                </div>
                <Button asChild size="lg" className="shrink-0">
                  <Link to="/ai-economy-jobs">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Explore AI Economy Jobs
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 text-center bg-primary/5">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold mb-4">Ready to Future-Proof Your Career?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get comprehensive AI risk analysis for any occupation and location
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90"
                onClick={handleGetReport}
              >
                <FileText className="w-5 h-5 mr-2" />
                Get Your Free AI Risk Report
              </Button>
            </div>
          </div>
        </section>

        {/* Free Report Modal */}
        {selectedOccupation && (
          <FreeReportModal
            isOpen={showPDFModal}
            onClose={() => setShowPDFModal(false)}
            occupation={selectedOccupation.title}
            area={selectedArea}
            soc={selectedOccupation.soc}
            source="ai_career_analysis"
          />
        )}
      </div>
    </>
  );
};

export default AICareerAnalysis;
